import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, DatePicker, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, KeyOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { label, date as fmtDate } from '../../lib/format';
export const ROLE_OPTIONS = [
  { value: 'SYSTEM_ADMIN', label: 'System Administrator' },
  { value: 'COMPANY_MANAGER', label: 'Company Manager' },
  { value: 'COMPANY_USER', label: 'Staff' },
  { value: 'CLIENT_ADMIN', label: 'Client portal user' },
];
export const STATUS_COLORS = { ACTIVE: 'green', INACTIVE: 'default', BLOCKED: 'red' };
const roleLabel = (v) => ROLE_OPTIONS.find((r) => r.value === v)?.label || v;

/** Staff list (xlsx Organization / Staff); the full personal + employment record is on each person's page. */
export default function UsersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [designations, setDesignations] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    const q = { company_id: companyId };
    Promise.all([
      request.get('/users', q).then((r) => r.json()),
      request.get('/departments', q).then((r) => r.json()),
      request.get('/designations', q).then((r) => r.json()),
    ])
      .then(([users, deps, levels]) => {
        if (users.status) setData(users.data);
        if (deps.status) setDepartments(deps.data);
        if (levels.status) setDesignations(levels.data.filter((d) => d.is_active));
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/users', { ...values, company_id: companyId, hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Staff member created' });
            setModalOpen(false);
            navigate(`/users/${r.data.id}`);
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  /** Admin reset: the API returns a one-time temporary password that the user must change at the next sign-in. */
  const resetPassword = (user) => {
    Modal.confirm({
      title: 'Reset the password for ' + user.name + '?',
      content: 'A temporary password will be generated and shown once. Every open session of this user will be signed out.',
      okText: 'Reset password',
      onOk: () =>
        request
          .post('/users/' + user.id + '/reset-password', {})
          .then((r) => r.json())
          .then((r) => {
            if (!r.status) return notification.error({ message: r.message });
            Modal.info({
              title: 'Temporary password for ' + user.name,
              content: (
                <div>
                  <Typography.Paragraph>Give this to the user in person or by phone. It works once; they will be asked to choose their own password at sign-in.</Typography.Paragraph>
                  <Typography.Text code copyable style={{ fontSize: 18 }}>
                    {r.data.temporary_password}
                  </Typography.Text>
                </div>
              ),
            });
          }),
    });
  };

  const setStatus = (user, status) => {
    request
      .put('/users/' + user.id, { status })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: status === 'ACTIVE' ? user.name + ' can sign in again' : user.name + ' has been deactivated' });
          fetchAll();
        } else notification.error({ message: r.message });
      });
  };

  const columns = [
    { title: 'Code', dataIndex: 'employee_code', width: 90, render: (v) => v || '-' },
    { title: 'Name', dataIndex: 'name', render: (v, u) => <Link to={`/users/${u.id}`}>{v}</Link> },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Department', key: 'dept', render: (_, u) => u.Department?.name || '-' },
    { title: 'Career level', key: 'level', render: (_, u) => (u.Designation ? `${u.Designation.level_order} - ${u.Designation.name}` : '-') },
    { title: 'Role', dataIndex: 'role', render: (v) => <Tag>{roleLabel(v)}</Tag> },
    { title: 'Hired', dataIndex: 'hire_date', render: (v) => fmtDate(v) },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
    ...(isManager
      ? [
          {
            title: '',
            key: 'actions',
            width: 230,
            render: (_, u) =>
              u.id === auth.user.id ? null : (
                <Space>
                  <Button size="small" icon={<KeyOutlined />} onClick={() => resetPassword(u)}>
                    Reset password
                  </Button>
                  {u.status === 'ACTIVE' ? (
                    <Button size="small" danger icon={<StopOutlined />} onClick={() => Modal.confirm({ title: 'Deactivate ' + u.name + '?', content: 'They will be signed out immediately and cannot sign in until reactivated.', okText: 'Deactivate', okButtonProps: { danger: true }, onOk: () => setStatus(u, 'INACTIVE') })}>
                      Deactivate
                    </Button>
                  ) : (
                    <Button size="small" icon={<CheckCircleOutlined />} onClick={() => setStatus(u, 'ACTIVE')}>
                      Reactivate
                    </Button>
                  )}
                </Space>
              ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Staff / Users
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          {isManager && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({ role: 'COMPANY_USER', employment_type: 'FULL_TIME' });
                setModalOpen(true);
              }}
            >
              New Staff Member
            </Button>
          )}
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={data} loading={loading} />

      <Modal title="New Staff Member" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate} okText="Create and open">
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="employee_code" label="Employee ID" style={{ width: 140 }}>
              <Input maxLength={30} placeholder="e.g. EMP-007" />
            </Form.Item>
            <Form.Item name="name" label="Full name" rules={[{ required: true }]} style={{ width: 320 }}>
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="email" label="Email (login)" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Temporary password"
            extra="At least 8 characters with a letter and a digit. The user is asked to choose their own password at the first sign-in."
            rules={[{ required: true, min: 8 }, { pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: 'Use both a letter and a digit' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="role" label="Role" rules={[{ required: true }]} style={{ width: 230 }}>
              <Select options={ROLE_OPTIONS.filter((r) => isSystemAdmin || r.value !== 'SYSTEM_ADMIN')} />
            </Form.Item>
            <Form.Item name="employment_type" label="Employment type" style={{ width: 230 }}>
              <Select options={[{ value: 'FULL_TIME', label: 'Full-time' }, { value: 'PART_TIME', label: 'Part-time' }, { value: 'INTERNSHIP', label: 'Internship' }]} />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="department_id" label="Department / team" style={{ width: 230 }}>
              <Select allowClear showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.name }))} />
            </Form.Item>
            <Form.Item name="designation_id" label="Career level" style={{ width: 230 }}>
              <Select allowClear options={designations.map((d) => ({ value: d.id, label: `${d.level_order} - ${d.name}` }))} />
            </Form.Item>
          </Space>
          <Form.Item name="hire_date" label="Date of hire" tooltip="Also the effective date of the starting career level">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
